import { portalApi, recordApi } from '../../../api';
import { Statuses } from '../../../constants/statuses';
import insuredKeys from '../../../services/models/insured/keys';
import { createMap } from '../../../utils/arrayUtil';
import { queryClient } from '../../../utils/queryClientUtil';
import { insuredRequirementTemplateBuilder } from '../../reducer';
import { createErrorToast, createSuccessToast, createToast } from '../app';
import { selectCompanyId } from '../company';
import { getAgentEditPolicyDefaultTemplate } from '../emails/defaults';
import { insuredListSearch } from '../insuredList';
import { itemSearch } from '../items';
import { getCurrentJobStatus, selectCurrentJobId, setJobStatus } from '../jobs';
import { selectUserId } from '../user';

/* Actions
================================================== */
const DEFAULT_STATE = {
  policies: null,
  allPolicies: [],
  archivedPolicies: [],
  activePolicies: [],
  requirements: { currentRequirement: null },
  loadingPolicies: {},
  loadingPolicyTypes: {},
  loadingDocuments: {},
  arePoliciesReloading: false,
  insured: null,
  emails: null,
  emailHistoryLoading: false,
  status: null,
  agentEmailModal: null,
  agentEmailModalLoading: true,
  isUpdating: false,
  policyLinkedItems: {},
  policyTypes: []
};

const INSUREDS_CREATE_INSURED = 'INSUREDS_GET_PAGE';
const INSUREDS_CREATE_INSURED_SUCCESS = 'INSUREDS_CREATE_INSURED_SUCCESS';
const INSUREDS_CREATE_INSURED_FAILURE = 'INSUREDS_CREATE_INSURED_FAILURE';

export const createInsured = insuredInfo => async (dispatch, getState) => {
  const companyId = selectCompanyId(getState());

  dispatch({ type: INSUREDS_CREATE_INSURED, companyId, insuredInfo });

  try {
    const response = await portalApi.createInsured(companyId, insuredInfo);

    dispatch({ type: INSUREDS_CREATE_INSURED_SUCCESS, newInsured: response });

    dispatch(createSuccessToast({
      title: 'Insured Successfully Saved!',
      duration: 6000
    }));

    return response;
  } catch (error) {
    dispatch({ type: INSUREDS_CREATE_INSURED_FAILURE, error });

    dispatch(createErrorToast({
      title: 'Uh oh, something went wrong',
      description: error.message,
      error
    }));

    return false;
  }
};

const INSUREDS_UPDATE_INSURED = 'INSUREDS_GET_PAGE';
const INSUREDS_UPDATE_INSURED_SUCCESS = 'INSUREDS_UPDATE_INSURED_SUCCESS';
const INSUREDS_UPDATE_INSURED_FAILURE = 'INSUREDS_UPDATE_INSURED_FAILURE';

export const updateInsured = (id, updates) => async dispatch => {
  dispatch({ type: INSUREDS_UPDATE_INSURED, id, updates });

  try {
    const response = await portalApi.updateInsured(id, updates);
    dispatch({ type: INSUREDS_UPDATE_INSURED_SUCCESS, updates: response });
    dispatch(createSuccessToast({
      title: 'Insured Successfully Saved!',
      duration: 6000
    }));

    return true;
  } catch (error) {
    dispatch({ type: INSUREDS_UPDATE_INSURED_FAILURE, error });
    dispatch(createErrorToast({
      title: 'Uh oh, something went wrong',
      description: error.message,
      error
    }));

    return false;
  }
};

const INSUREDS_GET_PAGE = 'INSUREDS_GET_PAGE';
const INSUREDS_GET_PAGE_SUCCESS = 'INSUREDS_GET_PAGE_SUCCESS';
const INSUREDS_GET_PAGE_FAILURE = 'INSUREDS_GET_PAGE_FAILURE';

export const getInsuredsPage = ({ filters } = {}) => async dispatch => {
  dispatch({ type: INSUREDS_GET_PAGE });

  const include = 'company,job_requirements,job_requirements.job';

  try {
    const response = await portalApi.getCompanyInsureds(filters, { include });
    dispatch({ type: INSUREDS_GET_PAGE_SUCCESS, ...response });
  } catch (error) {
    dispatch({
      type: INSUREDS_GET_PAGE_FAILURE,
      error
    });
  }
};

const INSUREDS_GET_INSURED = 'INSUREDS_GET_INSURED';
const INSUREDS_GET_INSURED_SUCCESS = 'INSUREDS_GET_INSURED_SUCCESS';
const INSUREDS_GET_INSURED_FAILURE = 'INSUREDS_GET_INSURED_FAILURE';
export const getInsured = insuredId => async dispatch => {
  dispatch({ type: INSUREDS_GET_INSURED, insuredId });

  try {
    const insured = await portalApi.getInsured(insuredId);
    dispatch({ type: INSUREDS_GET_INSURED_SUCCESS, insured });
    return insured;
  } catch (error) {
    dispatch({
      type: INSUREDS_GET_INSURED_FAILURE,
      error
    });
  }
};

const INSURED_GET_AGENT_DETAILS = 'INSURED_GET_AGENT_DETAILS';
const INSURED_GET_AGENT_DETAILS_SUCCESS = 'INSURED_GET_AGENT_DETAILS_SUCCESS';
const INSURED_GET_AGENT_DETAILS_FAILURE = 'INSURED_GET_AGENT_DETAILS_FAILURE';
export const getAgentDetails = (agentId, insuredId, jobId) => async dispatch => {
  dispatch({ type: INSURED_GET_AGENT_DETAILS });

  try {
    const agent = await portalApi.getAgent(agentId, jobId);
    const { insurance_requests: insuranceRequests } = agent;

    const relatedInsuranceRequestId = insuranceRequests.find(ir => (ir.insured_id === insuredId && ir.job_id === jobId))?.id;
    const agentName = agent.user_first_name ? agent.user_first_name : agent.user_email;
    agent.editPolicyTemplate = relatedInsuranceRequestId ? getAgentEditPolicyDefaultTemplate(agentName) : null;

    if (agent.companies.length) {
      const company = agent.companies[0];
      agent.companyName = company.company_name;
    }

    dispatch({ type: INSURED_GET_AGENT_DETAILS_SUCCESS, agent });
  } catch (error) {
    dispatch({
      type: INSURED_GET_AGENT_DETAILS_FAILURE,
      error
    });
  }
};

const INSURED_RESET_AGENT_EMAIL_MODAL = 'INSURED_RESET_AGENT_EMAIL_MODAL';
export const resetAgentEmailModal = () => dispatch => {
  dispatch({ type: INSURED_RESET_AGENT_EMAIL_MODAL });
};

const INSURED_GET_OVERALL_STATUS = 'INSURED_GET_OVERALL_STATUS';
const INSURED_GET_OVERALL_STATUS_SUCCESS = 'INSURED_GET_OVERALL_STATUS_SUCCESS';
const INSURED_GET_OVERALL_STATUS_FAILURE = 'INSURED_GET_OVERALL_STATUS_FAILURE';
export const getInsuredOverallStatus = () => async (dispatch, getState) => {
  const { insured } = getState().insured;
  dispatch({ type: INSURED_GET_OVERALL_STATUS });

  try {
    const response = await portalApi.getInsuredOverallStatus(insured.insuredId);
    dispatch({ type: INSURED_GET_OVERALL_STATUS_SUCCESS, status: response.status });
  } catch (error) {
    dispatch({ type: INSURED_GET_OVERALL_STATUS_FAILURE, error });
  }
};

const INSURED_RESET_CONTACT_INFO_MODAL = 'INSURED_RESET_CONTACT_INFO_MODAL';
export const resetContactInfoModal = () => dispatch => {
  dispatch({ type: INSURED_RESET_CONTACT_INFO_MODAL });
};

const INSURED_RESET_INSURED = 'INSURED_RESET_INSURED';
export const resetInsured = () => dispatch => {
  dispatch({ type: INSURED_RESET_INSURED });
};

const INSURED_GET_POLICY_LINKED_ITEMS = 'INSURED_GET_POLICY_LINKED_ITEMS';
const INSURED_GET_POLICY_LINKED_ITEMS_SUCCESS = 'INSURED_GET_POLICY_LINKED_ITEMS_SUCCESS';
const INSURED_GET_POLICY_LINKED_ITEMS_FAILURE = 'INSURED_GET_POLICY_LINKED_ITEMS_FAILURE';
export const getPolicyLinkedItems = (policyId, insuredId, pageNumber = 1, filter = {}) => async dispatch => {
  dispatch({ type: INSURED_GET_POLICY_LINKED_ITEMS });

  const include = [];
  const pageData = { pageNumber, pageSize: 10 };

  try {
    const { data, ...pageInfo } = await portalApi.getItemsForPolicy(policyId, insuredId, include, filter, pageData);
    dispatch({ type: INSURED_GET_POLICY_LINKED_ITEMS_SUCCESS, policyId, data, pageInfo });
  } catch (error) {
    dispatch({ type: INSURED_GET_POLICY_LINKED_ITEMS_FAILURE, error });
  }
};

const INSURED_GET_EMAILS = 'INSURED_GET_EMAILS';
const INSURED_GET_EMAILS_SUCCESS = 'INSURED_GET_EMAILS_SUCCESS';
const INSURED_GET_EMAILS_FAILURE = 'INSURED_GET_EMAILS_FAILURE';
export const getInsuredEmails = (
  insuredId,
  jobId = null,
  order = 'desc',
  pageDetails = { pageSize: 25, pageNumber: 1 },
  concatData = false
) => async dispatch => {
  dispatch({ type: INSURED_GET_EMAILS, insuredId, jobId, order, pageDetails, concatData });

  try {
    const { data, ...pageInfo } = await portalApi.getEmails(insuredId, jobId, order, pageDetails);
    dispatch({ type: INSURED_GET_EMAILS_SUCCESS, emails: data, pageInfo, concatData });
  } catch (error) {
    dispatch({ type: INSURED_GET_EMAILS_FAILURE, error });
  }
};

const INSURED_RESET_EMAILS = 'INSURED_RESET_EMAILS';
export const resetInsuredEmails = () => dispatch => {
  dispatch({ type: INSURED_RESET_EMAILS });
};

const INSURED_RESEND_EMAIL = 'INSURED_RESEND_EMAIL';
const INSURED_RESEND_EMAIL_SUCCESS = 'INSURED_RESEND_EMAIL_SUCCESS';
const INSURED_RESEND_EMAIL_FAILURE = 'INSURED_RESEND_EMAIL_FAILURE';
export const resendEmail = (emailId, onSuccess = () => {}, onFail = () => {}) => async dispatch => {
  dispatch({ type: INSURED_RESEND_EMAIL, emailId });

  dispatch(createToast({ title: 'Resending email...' }));
  try {
    const response = await portalApi.resendEmail(emailId);
    dispatch({ type: INSURED_RESEND_EMAIL_SUCCESS, response });
    dispatch(createSuccessToast({ title: 'Email Resent!' }));
    onSuccess();
  } catch (error) {
    dispatch({ type: INSURED_RESEND_EMAIL_FAILURE, error });
    dispatch(createErrorToast({ title: 'Email Failed to Resend', error }));
    onFail();
  }
};

const INSURED_LIST_INSURED_EXPORT_EXCEL = 'INSURED_LIST_INSURED_EXPORT_EXCEL';
const INSURED_LIST_INSURED_EXPORT_EXCEL_SUCCESS = 'INSURED_LIST_INSURED_EXPORT_EXCEL_SUCCESS';
const INSURED_LIST_INSURED_EXPORT_EXCEL_FAILURE = 'INSURED_LIST_INSURED_EXPORT_EXCEL_FAILURE';
export const exportListInsuredsToExcel = (companyId, searchParams) => async dispatch => {
  dispatch({ type: INSURED_LIST_INSURED_EXPORT_EXCEL, companyId, searchParams });

  createToast({ title: 'Downloading Excel Report' });
  try {
    const { excelLink } = await portalApi.exportListInsuredsExcel(companyId, searchParams);
    const link = document.createElement('a');
    link.href = excelLink;
    link.setAttribute('download', `Insureds - ${Date.now()}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    dispatch({ type: INSURED_LIST_INSURED_EXPORT_EXCEL_SUCCESS, excelLink });

    createToast({ title: 'Download Successful!', status: 'success' });
  } catch (error) {
    dispatch({ type: INSURED_LIST_INSURED_EXPORT_EXCEL_FAILURE, error });
    createToast({ title: 'Something went wrong with your download.', status: 'error' });
  }
};

const INSURED_SET_STAGED_REQUIREMENT_TEMPLATE = 'INSURED_SET_STAGED_REQUIREMENT_TEMPLATE';
export const stageInsuredDefaultRequirementTemplate = selected => dispatch => {
  dispatch({ type: INSURED_SET_STAGED_REQUIREMENT_TEMPLATE, selected });
};

const INSURED_SET_STAGED_BUSINESS_TYPE = 'INSURED_SET_STAGED_BUSINESS_TYPE';
export const stageInsuredBusinessType = selected => dispatch => {
  dispatch({ type: INSURED_SET_STAGED_BUSINESS_TYPE, selected });
};

const INSURED_SET_STAGED_COUNTRY = 'INSURED_SET_STAGED_COUNTRY';
export const stageInsuredCountry = selected => dispatch => {
  dispatch({ type: INSURED_SET_STAGED_COUNTRY, selected });
};

const INSURED_SET_CREATE_INSURED_STEP = 'INSURED_SET_CREATE_INSURED_STEP';
export const setCreateInsuredActiveStep = step => dispatch => {
  dispatch({ type: INSURED_SET_CREATE_INSURED_STEP, step });
};

const INSURED_GET_INSURANCE_REQUIREMENT = 'INSURED_GET_INSURANCE_REQUIREMENT';
const INSURED_GET_INSURANCE_REQUIREMENT_SUCCESS = 'INSURED_GET_INSURANCE_REQUIREMENT_SUCCESS';
const INSURED_GET_INSURANCE_REQUIREMENT_FAILURE = 'INSURED_GET_INSURANCE_REQUIREMENT_FAILURE';
export const getInsuranceRequirement = recordId => async dispatch => {
  dispatch({ type: INSURED_GET_INSURANCE_REQUIREMENT, recordId });

  try {
    const response = await portalApi.getRecord(recordId);
    dispatch({ type: INSURED_GET_INSURANCE_REQUIREMENT_SUCCESS, requirement: response });

    return response;
  } catch (error) {
    dispatch({ type: INSURED_GET_INSURANCE_REQUIREMENT_FAILURE, error });
    dispatch(createErrorToast({
      title: 'Uh oh, something went wrong',
      description: error.message,
      error
    }));

    return false;
  }
};

const INSURED_CREATE_INSURANCE_REQUIREMENT = 'INSURED_CREATE_INSURANCE_REQUIREMENT';
const INSURED_CREATE_INSURANCE_REQUIREMENT_SUCCESS = 'INSURED_CREATE_INSURANCE_REQUIREMENT_SUCCESS';
const INSURED_CREATE_INSURANCE_REQUIREMENT_FAILURE = 'INSURED_CREATE_INSURANCE_REQUIREMENT_FAILURE';
export const createInsuranceRequirement = (insuredId, jobId, requirementData, requirementTemplateId) => async (dispatch, getState) => {
  const userId = selectUserId(getState());

  const requirementInfo = {
    ...requirementData,
    owner_id: userId,
    requirement_template_id: requirementTemplateId
  };

  dispatch({ type: INSURED_CREATE_INSURANCE_REQUIREMENT, requirementData });

  try {
    const newRequirement = await portalApi.createInsuranceRequirement(insuredId, jobId, requirementInfo);
    dispatch({ type: INSURED_CREATE_INSURANCE_REQUIREMENT_SUCCESS, insuranceRequirement: newRequirement });

    return newRequirement;
  } catch (error) {
    dispatch({ type: INSURED_CREATE_INSURANCE_REQUIREMENT_FAILURE, error });
    dispatch(createErrorToast({
      title: 'Uh oh, something went wrong',
      description: 'Insured could not be added to project',
      error
    }));

    return false;
  }
};

const INSURED_UPDATE_INSURANCE_REQUIREMENTS = 'INSURED_UPDATE_INSURANCE_REQUIREMENTS';
const INSURED_UPDATE_INSURANCE_REQUIREMENTS_SUCCESS = 'INSURED_UPDATE_INSURANCE_REQUIREMENTS_SUCCESS';
const INSURED_UPDATE_INSURANCE_REQUIREMENTS_FAILURE = 'INSURED_UPDATE_INSURANCE_REQUIREMENTS_FAILURE';
export const updateInsuranceRequirements = (
  insuredId,
  insuranceRequirementId,
  requirementTemplateId,
  onSuccess = () => {},
  onFail = () => {},
  { extractedTemplateIds = [], sourceTemplateId = null } = {}
) => async dispatch => {
  dispatch({ type: INSURED_UPDATE_INSURANCE_REQUIREMENTS, insuredId, insuranceRequirementId, requirementTemplateId });

  dispatch(createToast({
    title: 'Saving Requirements...',
    duration: 60000
  }));

  try {
    const response = await portalApi.updateRecordRequirements(
      insuranceRequirementId,
      requirementTemplateId,
      { extractedTemplateIds, sourceTemplateId }
    );
    dispatch({ type: INSURED_UPDATE_INSURANCE_REQUIREMENTS_SUCCESS, requirements: response });

    await onSuccess();

    dispatch(createSuccessToast({
      title: 'Insured Requirements Updated',
      description: 'View updated requirements in the Required Policies table or by clicking "View Requirements" in the gear icon menu'
    }));
  } catch (error) {
    dispatch({ type: INSURED_UPDATE_INSURANCE_REQUIREMENTS_FAILURE, error });

    onFail();

    dispatch(createErrorToast({
      title: 'Uh oh, something went wrong',
      description: 'Insured requirements were not updated',
      error
    }));
  }
};

const INSURED_SET_AGENT_EMAIL_MODAL_LOADING = 'INSURED_SET_AGENT_EMAIL_MODAL_LOADING';

export const setAgentEmailModalLoading = value => dispatch => {
  dispatch({ type: INSURED_SET_AGENT_EMAIL_MODAL_LOADING, value });
};

const INSURED_ARCHIVE_INSURED = 'INSURED_ARCHIVE_INSURED';
const INSURED_ARCHIVE_INSURED_SUCCESS = 'INSURED_ARCHIVE_INSURED_SUCCESS';
const INSURED_ARCHIVE_INSURED_FAILURE = 'INSURED_ARCHIVE_INSURED_FAILURE';
export const archiveInsured = insuredId => async dispatch => {
  dispatch({ type: INSURED_ARCHIVE_INSURED, insuredId });

  try {
    await portalApi.archiveInsured(insuredId);
    dispatch({ type: INSURED_ARCHIVE_INSURED_SUCCESS });
    dispatch(insuredListSearch.getPage());
    dispatch(createSuccessToast({ title: 'Insured Archived' }));

    queryClient.invalidateQueries(insuredKeys.insuredAllRecords(insuredId));
    queryClient.invalidateQueries(insuredKeys.insuredActiveRecords(insuredId));
    queryClient.invalidateQueries(insuredKeys.insuredArchivedRecords(insuredId));
    queryClient.invalidateQueries(insuredKeys.insuredAllRecordsWithTrashed(insuredId));
    queryClient.invalidateQueries(insuredKeys.insuredOverallStatus(insuredId));
  } catch (error) {
    dispatch({ type: INSURED_ARCHIVE_INSURED_FAILURE, error });
    dispatch(createErrorToast({
      title: 'Uh oh, something went wrong',
      description: 'Insured was not archived',
      error
    }));
  }
};

const INSURED_REACTIVATE_INSURED = 'INSURED_REACTIVATE_INSURED';
const INSURED_REACTIVATE_INSURED_SUCCESS = 'INSURED_REACTIVATE_INSURED_SUCCESS';
const INSURED_REACTIVATE_INSURED_FAILURE = 'INSURED_REACTIVATE_INSURED_FAILURE';
export const reactivateInsured = insuredId => async dispatch => {
  dispatch({ type: INSURED_REACTIVATE_INSURED, insuredId });

  try {
    const response = await portalApi.reactivateInsured(insuredId);
    dispatch({ type: INSURED_REACTIVATE_INSURED_SUCCESS, insured: response });

    dispatch(insuredListSearch.getPage());
    dispatch(createSuccessToast({ title: 'Insured Activated' }));

    queryClient.invalidateQueries(insuredKeys.insuredAllRecords(insuredId));
    queryClient.invalidateQueries(insuredKeys.insuredActiveRecords(insuredId));
    queryClient.invalidateQueries(insuredKeys.insuredArchivedRecords(insuredId));
    queryClient.invalidateQueries(insuredKeys.insuredAllRecordsWithTrashed(insuredId));
    queryClient.invalidateQueries(insuredKeys.insuredOverallStatus(insuredId));
  } catch (error) {
    dispatch({ type: INSURED_REACTIVATE_INSURED_FAILURE, error });
    dispatch(createErrorToast({
      title: 'Uh oh, something went wrong',
      description: 'Insured was not reactivated',
      error
    }));
  }
};

const INSURED_DELETE_INSURED = 'INSURED_DELETE_INSURED';
const INSURED_DELETE_INSURED_SUCCESS = 'INSURED_DELETE_INSURED_SUCCESS';
const INSURED_DELETE_INSURED_FAILURE = 'INSURED_DELETE_INSURED_FAILURE';
export const deleteInsured = insuredId => async dispatch => {
  dispatch({ type: INSURED_DELETE_INSURED, insuredId });

  try {
    await portalApi.deleteInsured(insuredId);
    dispatch({ type: INSURED_DELETE_INSURED_SUCCESS });

    dispatch(insuredListSearch.getPage());
    dispatch(createSuccessToast({ title: 'Insured Deleted' }));

    queryClient.invalidateQueries(insuredKeys.insuredAllRecords(insuredId));
    queryClient.invalidateQueries(insuredKeys.insuredActiveRecords(insuredId));
    queryClient.invalidateQueries(insuredKeys.insuredArchivedRecords(insuredId));
    queryClient.invalidateQueries(insuredKeys.insuredAllRecordsWithTrashed(insuredId));
    queryClient.invalidateQueries(insuredKeys.insuredOverallStatus(insuredId));
  } catch (error) {
    dispatch({ type: INSURED_DELETE_INSURED_FAILURE, error });
    dispatch(createErrorToast({
      title: 'Uh oh, something went wrong',
      description: 'Insured was not deleted',
      error
    }));
  }
};

/* Reducer
================================================== */
const insuredsReducer = (state = DEFAULT_STATE, action) => {
  const { type, ...actionData } = action;

  switch (type) {
    case INSUREDS_CREATE_INSURED_SUCCESS:
      return { ...state, insured: action.newInsured };

    case INSUREDS_CREATE_INSURED_FAILURE:
      return { ...state, error: action.error };

    case INSUREDS_GET_PAGE_SUCCESS:
      return { ...state, ...actionData };

    case INSUREDS_GET_INSURED:
      return { ...state, insured: { insuredId: action.insuredId } };

    case INSUREDS_GET_INSURED_SUCCESS:
      return { ...state, insured: action.insured };

    case INSURED_GET_AGENT_DETAILS:
      return { ...state, agentEmailModalLoading: true };

    case INSURED_GET_AGENT_DETAILS_SUCCESS:
      return { ...state, agentEmailModal: action.agent, agentEmailModalLoading: false };

    case INSURED_GET_AGENT_DETAILS_FAILURE:
      return { ...state, error: action.error, agentEmailModalLoading: false };

    case INSURED_RESET_AGENT_EMAIL_MODAL:
      return { ...state, agentEmailModal: null, agentEmailModalLoading: true };

    case INSURED_GET_OVERALL_STATUS:
      return { ...state, ...actionData };

    case INSURED_GET_OVERALL_STATUS_SUCCESS:
      return { ...state, status: action.status };

    case INSURED_GET_OVERALL_STATUS_FAILURE:
      return { ...state, error: action.error };

    case INSURED_RESET_INSURED:
      return DEFAULT_STATE;

    case INSURED_GET_POLICY_LINKED_ITEMS_SUCCESS:
      return {
        ...state,
        policyLinkedItems: {
          ...state.policyLinkedItems,
          [action.policyId]: {
            data: action.data,
            pageInfo: action.pageInfo
          }
        }
      };

    case INSURED_GET_EMAILS: {
      if (action.concatData) return { ...state };
      return { ...state, emailHistoryLoading: true };
    }

    case INSURED_GET_EMAILS_SUCCESS: {
      if (action.concatData) {
        return {
          ...state,
          emailHistoryLoading: false,
          emails: {
            data: [
              ...state.emails.data,
              ...action.emails
            ],
            pageInfo: action.pageInfo
          }
        };
      }

      return {
        ...state,
        emailHistoryLoading: false,
        emails: {
          data: action.emails,
          pageInfo: action.pageInfo
        }
      };
    }

    case INSURED_GET_EMAILS_FAILURE:
      return { ...state, emailHistoryLoading: false, emails: { error: action.error } };

    case INSURED_RESET_EMAILS:
      return { ...state, emails: null };

    case INSURED_SET_STAGED_REQUIREMENT_TEMPLATE:
      return { ...state, stagedRequirementTemplate: action.selected };

    case INSURED_SET_STAGED_COUNTRY:
      return { ...state, stagedCountry: action.selected };

    case INSURED_SET_CREATE_INSURED_STEP:
      return { ...state, createInsuredStep: action.step };

    case INSURED_CREATE_INSURANCE_REQUIREMENT_SUCCESS:
      return { ...state, requirements: { ...state.requirements, currentRequirement: action.insuranceRequirement } };

    case INSURED_CREATE_INSURANCE_REQUIREMENT_FAILURE:
      return { ...state, error: action.error };

    case INSUREDS_UPDATE_INSURED_SUCCESS:
      return {
        ...state,
        insured: {
          ...state.insured,
          ...action.updates
        }
      };

    case INSURED_GET_INSURANCE_REQUIREMENT_SUCCESS: {
      return {
        ...state,
        requirements: {
          ...state.requirements,
          currentRequirement: action.requirement
        }
      };
    }

    case INSURED_SET_AGENT_EMAIL_MODAL_LOADING:
      return { ...state, agentEmailModalLoading: action.value };

    case INSURED_ARCHIVE_INSURED:
      return { ...state, isUpdating: true };

    case INSURED_ARCHIVE_INSURED_SUCCESS:
      return { ...state, isUpdating: false };

    case INSURED_ARCHIVE_INSURED_FAILURE:
      return { ...state, isUpdating: false };

    case INSURED_REACTIVATE_INSURED:
      return { ...state, isUpdating: true };

    case INSURED_REACTIVATE_INSURED_SUCCESS:
      return { ...state, isUpdating: false };

    case INSURED_REACTIVATE_INSURED_FAILURE:
      return { ...state, isUpdating: false };

    case INSURED_DELETE_INSURED:
      return { ...state, isUpdating: true };

    case INSURED_DELETE_INSURED_SUCCESS:
      return { ...state, isUpdating: false };

    case INSURED_DELETE_INSURED_FAILURE:
      return { ...state, isUpdating: false };

    default:
      return state;
  }
};

/* Selectors
================================================== */
export const selectInsured = state => state.insured.insured;
export const selectInsuredId = state => state.insured.insured.insuredId;
export const selectPolicyTypes = state => state.insured.policyTypes;
export const selectInsuredPolicies = state => state.insured.policies;
export const selectInsuredAllPolicies = state => state.insured.allPolicies;
export const selectInsuredArchivedPolicies = state => state.insured.archivedPolicies;
export const selectInsuredActivePolicies = state => state.insured.activePolicies;
export const selectInsuredRequirements = state => state.insured.requirements;
export const selectAgentEmailModal = state => state.insured.agentEmailModal;
export const selectInsuredOverallStatus = state => state.insured.status;
export const selectPolicyLinkedItems = state => state.insured.policyLinkedItems;
export const selectIsPolicyLoading = (policyId, insurancePolicyTypeId) => state => (policyId
  ? !!state.insured.loadingPolicies[policyId]
  : !!state.insured.loadingPolicyTypes[insurancePolicyTypeId]);
export const selectIsDocumentTypeLoading = documentTypeId => state => !!state.insured.loadingDocuments[documentTypeId];
export const selectAgentEmailModalLoading = state => state.insured.agentEmailModalLoading;
export const selectInsuredEmails = state => state.insured.emails?.data;
export const selectInsuredEmail = emailId => state => state.insured.emails.data.find(email => email.id === parseInt(emailId, 10));
export const selectInsuredEmailsPageData = state => state.insured.emails?.pageInfo;
export const selectEmailHistoryLoading = state => state.insured.emailHistoryLoading;
export const selectEmailHistoryError = state => state.insured.emails?.error;
export const selectCreateInsuredStep = state => state.insured.createInsuredStep;
export const selectStagedInsuredCountry = state => state.insured.stagedCountry;
export const selectPoliciesAreReloading = state => state.insured.arePoliciesReloading;
export const selectEditPolicyTemplate = state => state.insured.agentEmailModal?.editPolicyTemplate;
export const selectCurrentRequirementId = state => state.insured.requirements?.currentRequirement?.id;
export const selectCurrentRequirement = state => state.insured.requirements?.currentRequirement;
export const selectInsuredUpdating = state => state.insured.isUpdating;

export default insuredsReducer;

/* Utils
================================================== */
export function v2MatchPoliciesToRequirements(policyTypes, policies, requirements, includeNotReceived = true) {
  const requirementPolicyTypeMap = createMap(requirements, 'insurance_policy_type_id', true);
  const requirementPolicyTypePropertyMap = createMap(requirements, 'insurance_policy_type_property_id');
  const receivedPolicyTypeIds = [];

  const policiesWithRequirements = policies.filter(p => (!p.archived_at && !p.deleted_at))
    .reduce((enhancedRequirements, policy) => {
      const policyTypeId = policy.insurance_policy_type_id;
      const policyPropertyValueMap = createMap(policy.property_values, 'insurance_policy_type_property_id');

      receivedPolicyTypeIds.push(policyTypeId);

      const requirementPolicies = requirementPolicyTypeMap[policyTypeId] ?? null;

      let status = requirementPolicies ? requirementPolicies[0].status : Statuses.NotRequired;
      const sortOrder = requirementPolicies ? requirementPolicies[0].sort_order : 0;
      const recordId = requirementPolicies ? requirementPolicies[0].insurance_requirement_id : null;

      // catch any requirements w/ not_received status
      if (status === Statuses.NotReceived && !!policy.id) {
        status = Statuses.Pending;

        recordApi.updateRequirementStatuses({
          recordId,
          policyId: policy.id,
          status: Statuses.Pending,
          logActivity: false
        });
      }

      // property mapping
      const allProperties = policy.insurance_policy_type.properties.map(property => {
        const requirementProperty = requirementPolicyTypePropertyMap[property.id] ?? {};
        const { required_value: maxRequirement = null } = requirementProperty;

        if (policyPropertyValueMap[property.id]) {
          return {
            ...policyPropertyValueMap[property.id],
            maxRequirement
          };
        }

        return {
          insurance_policy_type_property_id: property.id,
          value: null,
          insurance_policy_type_property: property,
          maxRequirement
        };
      });

      return [...enhancedRequirements, {
        ...policy,
        statusLoading: false,
        property_values: allProperties,
        requirementStatus: status,
        sortOrder
      }];
    }, []);

  const notReceivedPolicies = !includeNotReceived
    ? []
    : Object.entries(requirementPolicyTypeMap)
      .map(([policyTypeId, requirement]) => ([parseInt(policyTypeId, 10), requirement]))
      .reduce((requirementsWithoutPolicies, [policyTypeId, requirementProperties]) => {
        if (receivedPolicyTypeIds.includes(policyTypeId)) {
          return requirementsWithoutPolicies;
        }

        const policyType = policyTypes.find(t => t.id === policyTypeId);

        const requirementStatus = requirementProperties.length
          ? requirementProperties[0].status
          : Statuses.NotReceived;

        const sortOrder = requirementProperties.length ? requirementProperties[0].sort_order : 0;

        const allProperties = requirementProperties.map(requirementProperty => ({
          insurance_policy_type_property_id: requirementProperty.insurance_policy_type_property_id,
          insurance_policy_type_property: policyType.properties.find(p => p.id === requirementProperty.insurance_policy_type_property_id),
          value: null,
          maxRequirement: requirementProperty.required_value
        }));

        return [...requirementsWithoutPolicies, {
          insurance_policy_type: policyType,
          insurance_policy_type_id: policyTypeId,
          statusLoading: false,
          propertyValues: allProperties,
          requirementStatus,
          sortOrder
        }];
      }, []);

  const allPolicies = [...policiesWithRequirements, ...notReceivedPolicies];
  return allPolicies.sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 *
 * DEPRECATED
 *
 * Leaving for reference
 *
 * This returns a function with a closure to keep track of
 * previous statuses for a specific policy Type. This is temporary
 * until the UI supports altering requirement statuses at the property
 * level.
 *
 * @returns {function(*=, *=): *}
 */
// function getStatusCalculator() {
//   const previousStatuses = {};
//
//   function calculateStatus(policy, requirementStatus, previousStatus = null) {
//     if (policy.status === Statuses.Archived || policy.archived_at) return Statuses.Archived;
//     if (policy.status === Statuses.Deleted || policy.deleted_at) return Statuses.Deleted;
//     if (policy.status === Statuses.Canceled) return Statuses.Canceled;
//
//     if (requirementStatus === Statuses.Waived || previousStatus === Statuses.Waived) {
//       return Statuses.Waived;
//     }
//
//     if (isPast(policy.expires_at)) {
//       return Statuses.Expired;
//     }
//
//     if (requirementStatus === Statuses.Pending || previousStatus === Statuses.Pending) {
//       return Statuses.Pending;
//     }
//
//     if (requirementStatus === Statuses.NonCompliant || previousStatus === Statuses.NonCompliant) {
//       return Statuses.NonCompliant;
//     }
//
//     if (requirementStatus === Statuses.Compliant || previousStatus === Statuses.Compliant) {
//       return Statuses.Compliant;
//     }
//
//     if (requirementStatus === Statuses.NotReceived || previousStatus === Statuses.NotReceived) {
//       return Statuses.NotReceived;
//     }
//
//     return requirementStatus;
//   }
//
//   return (policy, status) => {
//     const policyTypeId = policy.insurance_policy_type_id;
//     const currentStatus = previousStatuses[policyTypeId];
//     previousStatuses[policyTypeId] = calculateStatus(policy, status, currentStatus);
//     return previousStatuses[policyTypeId];
//   };
// }

/**
 * DEPRECATED: matchPoliciesToRequirements
 *
 * Leaving for reference
 *
 * @param policyTypes
 * @param policies
 * @param requirements
 * @param includeNotReceived
 * @returns {function(*): *&{property_values: *, statusLoading: boolean}}
 */
// export function matchPoliciesToRequirements(policyTypes, policies, requirements, includeNotReceived = true) {
//   const requirementMap = createMap(requirements, 'policy_type_id', true);
//   const receivedPolicyTypeIds = [];
//
//   const policiesWithRequirements = policies
//     .filter(p => (!p.archived_at && !p.deleted_at))
//     .reduce((enhancedRequirements, policy) => {
//       receivedPolicyTypeIds.push(policy.insurance_policy_type_id);
//
//       const statusCalculator = getStatusCalculator();
//       const policyPropertyValueMap = createMap(policy.property_values, 'insurance_policy_type_property_id');
//       let requirementStatus = Statuses.NotReceived;
//       let policyHasRequiredProperties = false;
//       let requirementPropertyOverrideStatus = Statuses.NotRequired;
//
//       const allProperties = policy.insurance_policy_type.properties.map(property => {
//         const requirementProperty = requirements.find(r => r.property_id === property.id) || {};
//         const policyProvidesRequiredValue = !!policyPropertyValueMap[property.id];
//
//         const {
//           required_value: maxRequirement = null,
//           status,
//           requirement_property_status: requirementPropertyStatus
//         } = requirementProperty;
//
//         let currentStatus = status || Statuses.NotRequired;
//
//         // If a requirement exists and the policy has a value for it, we will run the calculation as usual
//         if (maxRequirement !== null && policyProvidesRequiredValue) {
//           policyHasRequiredProperties = true;
//
//           // But... if a requirement does exist and the policy does not provide the required value
//           // Then we override the calculated value with either what's stored in the db, or set it to pending.
//           // If the policy provides only values that are not required, then we assign not required
//         } else if (requirementPropertyStatus && currentStatus !== Statuses.NotRequired) {
//           if (requirementPropertyStatus === Statuses.NotReceived) {
//             // Requirements with provided policies should never render not received, so we override the db if the
//             // requirement properties have not_received statuses.
//             requirementPropertyOverrideStatus = Statuses.Pending;
//           } else {
//             requirementPropertyOverrideStatus = requirementPropertyStatus;
//           }
//         }
//
//         // If a policy exists with no property values, the calculated status is not received
//         // This happens from agents not submitting full policies, fixed by DEV-1501 and deployed 1/08/23.
//         // So we force the status to the requirement property status for any policies that slipped through before then
//         if (!Object.values(policyPropertyValueMap).length && status === Statuses.NotReceived) {
//           currentStatus = requirementPropertyStatus;
//         }
//
//         requirementStatus = statusCalculator(policy, currentStatus);
//
//         if (policyPropertyValueMap[property.id]) {
//           return {
//             ...policyPropertyValueMap[property.id],
//             maxRequirement
//           };
//         }
//
//         return {
//           insurance_policy_type_property_id: property.id,
//           value: null,
//           insurance_policy_type_property: property,
//           maxRequirement
//         };
//       });
//
//       return [...enhancedRequirements, {
//         ...policy,
//         statusLoading: false,
//         property_values: allProperties,
//         requirementStatus: policyHasRequiredProperties ? requirementStatus : requirementPropertyOverrideStatus
//       }];
//     }, []);
//
//   const notReceivedPolicies = !includeNotReceived
//     ? []
//     : Object.entries(requirementMap)
//       .map(([policyTypeId, requirement]) => ([parseInt(policyTypeId, 10), requirement]))
//       .reduce((requirementsWithoutPolicies, [policyTypeId, requirementProperties]) => {
//         if (receivedPolicyTypeIds.includes(policyTypeId)) {
//           return requirementsWithoutPolicies;
//         }
//
//         const policyType = policyTypes.find(t => t.id === policyTypeId);
//         const statusCalculator = getStatusCalculator();
//         let requirementStatus = Statuses.NotReceived;
//
//         const allProperties = requirementProperties.map(requirementProperty => {
//           requirementStatus = statusCalculator({ insurance_policy_type_id: policyTypeId }, requirementProperty.status);
//
//           return {
//             insurance_policy_type_property_id: requirementProperty.property_id,
//             insurance_policy_type_property: policyType.properties.find(p => p.id === requirementProperty.property_id),
//             value: null,
//             maxRequirement: requirementProperty.required_value
//           };
//         });
//
//         return [...requirementsWithoutPolicies, {
//           insurance_policy_type: policyType,
//           insurance_policy_type_id: policyTypeId,
//           statusLoading: false,
//           propertyValues: allProperties,
//           requirementStatus
//         }];
//       }, []);
//
//   return [...policiesWithRequirements, ...notReceivedPolicies];
// }
